'use client'

/**
 * Purpose: Survey axis slider (0–100) between two opposing skin traits, e.g. oily ↔ dry.
 * Inputs: `value`, `onChange`, the left/right trait labels, and the page title/description.
 * Outputs: A native range input with explicit ARIA value attributes and an arrow/Home/End/Page
 * keyboard handler so the value is operable and announced without a pointer.
 */

import type { KeyboardEvent } from 'react'
import './skinAxisSlider.css'

type Props = {
  value: number
  onChange: (v: number) => void
  leftLabel: string
  rightLabel: string
  title: string
  description: string
}

const MIN = 0
const MAX = 100
const STEP = 1
// PageUp/PageDown jump a tenth of the range, per the WAI-ARIA slider pattern.
const PAGE_STEP = 10

const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n))

/** Map a slider keydown to its next value; `null` means the key is not ours. */
function nextSliderValue(key: string, current: number): number | null {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return clamp(current + STEP)
    case 'ArrowLeft':
    case 'ArrowDown':
      return clamp(current - STEP)
    case 'PageUp':
      return clamp(current + PAGE_STEP)
    case 'PageDown':
      return clamp(current - PAGE_STEP)
    case 'Home':
      return MIN
    case 'End':
      return MAX
    default:
      return null
  }
}

export function SkinAxisSlider({ value, onChange, leftLabel, rightLabel, title, description }: Props) {
  // Handle keys ourselves (and preventDefault) so the value change is deterministic and not
  // doubled by the browser's native range behaviour.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const next = nextSliderValue(e.key, value)
    if (next === null) return
    e.preventDefault()
    if (next !== value) onChange(next)
  }

  return (
    <div id="skin-axis-slider" className="skin-slider-wrapper">
      <h1 id="skin-axis-slider-title" className="mb-2">{title}</h1>
      <p id="skin-axis-slider-description" className="skin-slider-description">{description}</p>
      <div id="skin-axis-slider-controls" className="skin-slider-controls">
        <span id="skin-axis-slider-left-label" className="skin-slider-side-label skin-slider-side-label--left">{leftLabel}</span>
        {/* Explicit aria-value* mirror the native attributes so assistive tech always gets them. */}
        <input
          id="skin-axis-slider-input"
          type="range"
          min={MIN}
          max={MAX}
          step={STEP}
          value={value}
          onChange={(e) => {
            const next = Number(e.target.value)
            if (Number.isFinite(next)) onChange(clamp(next))
          }}
          onKeyDown={handleKeyDown}
          aria-label={title}
          aria-describedby="skin-axis-slider-description"
          aria-valuemin={MIN}
          aria-valuemax={MAX}
          aria-valuenow={value}
          aria-valuetext={`${value} — ${leftLabel} ${MIN}, ${rightLabel} ${MAX}`}
          className="skin-slider-input"
        />
        <span id="skin-axis-slider-right-label" className="skin-slider-side-label">{rightLabel}</span>
      </div>
    </div>
  )
}
